import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Road Construction",
  "Paver / Kerb Works",
  "Granite Supply",
  "Sewerage & Drainage",
  "Stormwater Management",
  "Boundary Walls",
  "Rainwater Harvesting",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSuccess(true);
      toast.success("Inquiry received. Our team will be in touch shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        project_type: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Failed to submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Failed to submit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-ink text-bone relative overflow-hidden grain"
    >
      <Toaster position="top-center" />

      {/* Marquee strip */}
      <div className="border-y border-bone/15 overflow-hidden py-8 md:py-10">
        <div className="marquee-track font-heading font-black text-6xl md:text-8xl uppercase tracking-tighter text-bone whitespace-nowrap">
          <span className="px-6">LET'S BUILD ·</span>
          <span className="px-6 text-rust italic font-medium">Together</span>
          <span className="px-6">· LET'S BUILD ·</span>
          <span className="px-6 text-rust italic font-medium">Together</span>
          <span className="px-6">· LET'S BUILD ·</span>
          <span className="px-6 text-rust italic font-medium">Together</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — info */}
          <div className="lg:col-span-5">
            <span className="tech-rule">06 — Contact</span>
            <h2
              data-testid="contact-heading"
              className="mt-6 font-heading font-black uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tighter"
            >
              Start a project<br />
              <span className="italic font-medium text-rust">with us.</span>
            </h2>
            <p className="mt-6 text-bone/70 leading-relaxed max-w-md">
              Share scope, timeline and location. Our project office responds
              within one business day.
            </p>

            <div className="mt-10 space-y-6">
              <InfoRow
                icon={<MapPin className="w-4 h-4" />}
                label="Head Office"
                lines={[COMPANY.address]}
              />
              <InfoRow
                icon={<Phone className="w-4 h-4" />}
                label="Phone"
                lines={COMPANY.phones}
                hrefs={COMPANY.phones.map((p) => `tel:${p.replace(/\s+/g, "")}`)}
              />
              <InfoRow
                icon={<Mail className="w-4 h-4" />}
                label="Email"
                lines={[COMPANY.email]}
                hrefs={[`mailto:${COMPANY.email}`]}
              />
            </div>

            <div className="mt-10 p-6 border border-bone/20">
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-rust mb-2">
                GSTIN
              </div>
              <div className="font-mono text-sm tracking-wider text-bone">
                {COMPANY.gstin}
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-bone/25 p-6 md:p-10 bg-ink/40 backdrop-blur-sm"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  testid="contact-input-name"
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  testid="contact-input-email"
                />
                <Field
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  testid="contact-input-phone"
                />
                <Field
                  label="Company / Developer"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  testid="contact-input-company"
                />
                <div className="md:col-span-2">
                  <Label>Project Type</Label>
                  <select
                    name="project_type"
                    value={form.project_type}
                    onChange={onChange}
                    data-testid="contact-select-project-type"
                    className="w-full bg-transparent border border-bone/30 px-4 py-3 text-bone outline-none focus:border-rust transition-colors"
                  >
                    <option value="" className="bg-ink">
                      Select a scope…
                    </option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-ink">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label>Project Brief</Label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    required
                    data-testid="contact-textarea-message"
                    placeholder="Tell us about the site, scope, and tentative timeline…"
                    className="w-full bg-transparent border border-bone/30 px-4 py-3 text-bone placeholder:text-bone/35 outline-none focus:border-rust transition-colors resize-none"
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-bone/50 font-mono tracking-wider uppercase">
                  We respond within 1 business day.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-3 bg-rust text-bone px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-rust-hover transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Inquiry <Send className="w-4 h-4" strokeWidth={2.5} />
                    </>
                  )}
                </button>
              </div>

              {success && (
                <div
                  data-testid="contact-success"
                  className="mt-6 p-4 border border-rust/60 bg-rust/10 text-bone flex items-center gap-3"
                >
                  <ArrowUpRight className="w-4 h-4 text-rust" />
                  <span className="text-sm">
                    Inquiry submitted. We'll be in touch shortly.
                  </span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Label({ children }) {
  return (
    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-bone/60 mb-2">
      {children}
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-rust"> *</span>}
      </Label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-bone/30 px-4 py-3 text-bone placeholder:text-bone/35 outline-none focus:border-rust transition-colors"
      />
    </div>
  );
}

function InfoRow({ icon, label, lines, hrefs }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 w-9 h-9 border border-bone/30 grid place-items-center">
        {icon}
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-rust mb-1">
          {label}
        </div>
        <div className="space-y-0.5">
          {lines.map((l, i) =>
            hrefs?.[i] ? (
              <a
                key={l}
                href={hrefs[i]}
                className="block text-bone hover:text-rust transition-colors"
              >
                {l}
              </a>
            ) : (
              <div key={l} className="text-bone/85">
                {l}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
